import React, { FC, useRef, useState } from 'react';
import { format, isValid, parse } from 'date-fns';
import nb from 'date-fns/locale/nb';
import FocusTrap from 'focus-trap-react';
import { usePopper } from 'react-popper';

import { useInsuranceLabels } from '../../../../Utils/Context/labelsContext';
import { Calendar } from '../../../../Partials/Calendar';
import { Tooltip } from '../../../../Partials/Tooltip';

import { BookingCalendarControls } from './BookingCalendarControls';
import { BookingCalendarInput } from './BookingCalendarInput';
import classNames from 'classnames';

interface BookingCalendarProps {
  onChange: (date: Date) => void;
  disabled?: boolean;
}

const DATE_FORMAT = "dd-MM-y"

export const BookingCalendar: FC<BookingCalendarProps> = (props) => {
  const { onChange, disabled = false } = props;

  const {
    inputs
  } = useInsuranceLabels();

  const [selectedDate, setSelectedDate] = useState<Date>();
  const [inputValue, setInputValue] = useState('');
  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const popperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const popper = usePopper(popperRef.current, popperElement, {
    placement: 'bottom-start'
  });

  const handleInputChange = (value: string) => {
    setInputValue(value);
    const date = parse(value, DATE_FORMAT, new Date(), { locale: nb });

    if (isValid(date)) {
      setSelectedDate(date);
      onChange(date);
    } else {
      setSelectedDate(undefined);
    }
  };

  const handleDateSelect = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    } else {
      setInputValue('');
    }
  }

  const openPopper = () => {
    setIsPopperOpen(true);
  };

  const closePopper = () => {
    setIsPopperOpen(false);
    buttonRef?.current?.focus();
  };

  const onAcceptClick = () => {
    if (selectedDate) {
      onChange(selectedDate);
      setInputValue(format(selectedDate, DATE_FORMAT, { locale: nb }));
    }
    setIsPopperOpen(false);
  };

  const onCancelClick = () => {
    setIsPopperOpen(true);
    closePopper();
  };

  return (
    <div className="bookingCalendar">
      <Tooltip
        text={inputs.dateTooltip}
        placement='bottom-start'
        anchorClassName={classNames("bookingCalendar__tooltip", { ["bookingCalendar__tooltip--disabled"]: disabled })}
      />
      <div className="bookingCalendar__input" ref={popperRef}>
       <BookingCalendarInput
        inputValue={inputValue}
        handleInputChange={handleInputChange}
        onButtonClick={openPopper}
        ref={buttonRef}
        disabled={disabled}
       />
      </div>
      {isPopperOpen && (
        <FocusTrap
          active
          focusTrapOptions={{
            allowOutsideClick: true,
            clickOutsideDeactivates: true,
            onDeactivate: closePopper
          }}
        >
          <div
            tabIndex={-1}
            style={popper.styles.popper}
            className="bookingCalendar__calendarWrapper"
            {...popper.attributes.popper}
            ref={setPopperElement}
            role="dialog"
          >
            <Calendar
              className="bookingCalendar__calendar"
              onDayPick={handleDateSelect}
              selectedDate={selectedDate}
            />
            <BookingCalendarControls
              onAccept={onAcceptClick}
              onCancel={onCancelClick}
            />
          </div>
        </FocusTrap>
      )}
    </div>
  )
};
